import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gamepad2, Users } from 'lucide-react';
import predictionService, { PredictionGame, coverSlidesFor, coverSlideMetaFor } from '../../services/predictionService';
import sponsorTracking from '../../services/sponsorTracking';
import PredictionCoverSlider from '../predictions/PredictionCoverSlider';
import { t } from '../../i18n';
import DynamicSlider from '../slider/DynamicSlider';

/** Slider-Fläche der Startseiten-Sektion „Tippspiele“ (Marcel 2026-09-29): Desktop rechts neben den Karten, Mobile darunter */
const HOME_PREDICTION_GAMES_SLIDER_LOCATION = 'home_prediction_games';

const ACCENT = '#3fced1';

/**
 * Startseiten-Slider: aktive Tippspiele (open/running) als horizontale
 * Karten — direkt unter "Neu bei DartCloud" (Marcel 2026-08-27).
 * Stil-Parität zum HomePdcCalendar; ohne aktive Spiele ausgeblendet.
 */
const HomePredictionGames: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [games, setGames] = useState<PredictionGame[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    predictionService
      .getAllGames()
      .then((list) => {
        if (cancelled) return;
        setGames(
          (list || []).filter(
            (g) =>
              g &&
              (g.status === 'open' || g.status === 'running') &&
              g.isActive !== false
          )
        );
      })
      .catch(() => {
        /* Section bleibt einfach ausgeblendet */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // CLS-Fix (2026-09-01): Skeleton in Kartenhöhe (4:3-Bild 165px + Textblock)
  // statt null, solange die Tippspiele laden — erst ohne aktive Spiele
  // kollabiert die Sektion.
  if (!loaded) {
    return (
      <section aria-label={t('web.component.home-prediction-games.tippspiele')} aria-hidden="true">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-prediction-games.tippspiele')}</h2>
        </div>
        <div className="flex gap-3 overflow-hidden pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="flex w-[220px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900"
            >
              <div className="aspect-video w-full animate-pulse bg-zinc-800" />
              <div className="p-3">
                <div className="mb-2 h-[19px] w-24 animate-pulse rounded-full bg-zinc-800" />
                <div className="h-3.5 w-4/5 animate-pulse rounded bg-zinc-800" />
                <div className="mt-3 h-3 w-2/5 animate-pulse rounded bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (games.length === 0) return null;

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const openGame = (game: PredictionGame) => {
    const identifier = predictionService.getGameIdentifier(game);
    if (identifier) navigate(`/tippspiele/${identifier}`);
  };

  return (
    <section aria-label={t('web.component.home-prediction-games.tippspiele')}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-prediction-games.tippspiele')}</h2>
        <div className="flex items-center gap-2">
          <Link
            to="/tippspiele"
            className="text-sm font-semibold transition-colors hover:underline"
            style={{ color: ACCENT }}
          >
            {t('web.component.home-prediction-games.alle-tippspiele')}{' '}<ChevronRight size={14} className="inline" />
          </Link>
          <button
            type="button"
            aria-label={t('web.component.featured-events-slider.zurueck-scrollen')}
            onClick={() => scrollBy(-360)}
            className="hidden rounded-lg border border-zinc-800 bg-zinc-900 p-1.5 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:block"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            aria-label={t('web.component.featured-events-slider.weiter-scrollen')}
            onClick={() => scrollBy(360)}
            className="hidden rounded-lg border border-zinc-800 bg-zinc-900 p-1.5 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:block"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="lg:flex lg:items-start lg:gap-4">
      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {games.map((game) => {
          const image = predictionService.buildImageUrl(game.image);
          const isOpen = game.status === 'open';
          return (
            <button
              key={game._id || game.slug}
              type="button"
              onClick={() => openGame(game)}
              className="group flex w-[220px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
            >
              {/* Titelbild fest 16:9 (Marcel 2026-09-22): erst Tippspiel-Bild, dann
                  Sponsoren-Bilder als Auto-Slider (nur wenn Sponsoren mit Bild) */}
              <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800" data-testid="home-prediction-cover">
                <PredictionCoverSlider
                  slides={image ? coverSlidesFor(game) : []}
                  alt={game.name || t('web.component.home-prediction-games.tippspiel')}
                  className="h-full w-full"
                  onSlideShown={(i) => { const m = coverSlideMetaFor(game)[i]; if (m?.sponsorId) sponsorTracking.impression({ gameId: game._id ?? game.id, gameSlug: game.slug, sponsorId: m.sponsorId, sponsorTitle: m.sponsorTitle, placement: 'home_cover' }); }}
                  fallback={(
                    <div className="flex h-full w-full items-center justify-center bg-[#3fced1]/10">
                      <Gamepad2 size={32} className="text-[#3fced1]/80" />
                    </div>
                  )}
                />
              </div>
              <div className="flex flex-1 flex-col p-3">
                <span
                  className={`mb-2 inline-flex self-start rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    isOpen ? 'bg-[#3fced1]/15 text-[#3fced1]' : 'bg-red-500/15 text-red-400'
                  }`}
                >
                  {isOpen ? t('web.component.home-prediction-games.anmeldung-offen') : t('web.component.event-timeline-card.laeuft')}
                </span>
                <h3 className="line-clamp-2 text-[13.5px] font-semibold leading-snug text-white">
                  {game.name || t('web.component.home-prediction-games.tippspiel')}
                </h3>
                <div className="mt-auto flex items-center gap-1 pt-2 text-[11px] font-medium text-gray-500">
                  <Users size={12} />
                  {t('web.component.home-prediction-games.teilnehmer', { value: game.stats?.totalParticipants ?? 0 })}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {/* Desktop: rechts neben den Karten (leer → nichts) */}
      <DynamicSlider location={HOME_PREDICTION_GAMES_SLIDER_LOCATION} className="hidden lg:block lg:w-[400px] lg:shrink-0" />
      </div>
      {/* Mobile: unter der Sektion */}
      <DynamicSlider location={HOME_PREDICTION_GAMES_SLIDER_LOCATION} className="mt-3 w-full lg:hidden" />
    </section>
  );
};

export default HomePredictionGames;
